import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSeo from "@/lib/useSeo";
import { ArrowRight, BookOpen } from "lucide-react";

export default function Curriculum() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [error, setError] = useState(false);

  useSeo(`${t("curr.title")} — AI Lernwerk`, t("curr.sub"));

  useEffect(() => {
    setError(false);
    api.get(`/modules?lang=${lang}`)
      .then((r) => setModules(r.data))
      .catch(() => setError(true));
  }, [lang]);

  return (
    <div className="min-h-screen bg-void">
      <Navbar />
      <main className="pt-28 pb-16 max-w-4xl mx-auto px-6 md:px-12" data-testid="curriculum-page">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-gold text-sm tracking-[0.3em] uppercase mb-3">{t("curr.eyebrow")}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink mb-4 leading-tight">{t("curr.title")}</h1>
          <p className="text-ink-soft text-lg leading-relaxed max-w-2xl mb-6">{t("curr.sub")}</p>
          <Link to={user && user !== false ? "/dashboard" : "/register"} data-testid="curriculum-cta"
            className="inline-flex items-center gap-2 bg-gold text-void px-6 py-3 font-medium hover:bg-gold-hover transition-colors">
            {t("curr.cta")} <ArrowRight size={18} strokeWidth={1.5} />
          </Link>
        </motion.div>

        <div className="mt-16 space-y-14">
          {error && (
            <p className="text-ink-soft" data-testid="curriculum-error">{t("admin.error")}</p>
          )}
          {!error && modules.length === 0 && (
            <p className="text-ink-soft" data-testid="curriculum-loading">{t("common.loading")}</p>
          )}
          {modules.map((m, i) => (
            <section key={m.module_id} id={m.module_id} className="scroll-mt-24" data-testid={`curriculum-module-${m.module_id}`}>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-mono text-xs text-gold-muted">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-serif text-3xl text-ink leading-snug">{m.title}</h2>
              </div>
              <p className="text-ink-muted mb-5 max-w-2xl">{m.description}</p>
              <ul className="border border-ink/5 divide-y divide-ink/5">
                {m.lessons.map((l) => (
                  <li key={l.lesson_id}>
                    <Link to={`/learn/${l.lesson_id}`} data-testid={`curriculum-lesson-${l.lesson_id}`}
                      className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-graphite transition-colors group">
                      <span className="flex items-center gap-3 min-w-0">
                        <span className="font-mono text-xs text-ink-muted shrink-0">{String(l.order).padStart(2, "0")}</span>
                        <span className="text-ink group-hover:text-gold transition-colors">{l.title}</span>
                      </span>
                      <span className="hidden sm:flex items-center gap-1 text-ink-muted text-sm shrink-0 group-hover:text-gold transition-colors">
                        <BookOpen size={15} strokeWidth={1.5} /> {t("curr.read")}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
